import { FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import { Nullable } from '@/utils/types';
import { IFilterConfigChildren, IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import {
  CreatedUpdatedByFilterValue,
  getBuildingDimensionRangeFormattedValue,
  getCityComboboxFormattedValue,
  getCreatedUpdatedBySelectFormattedValue,
  getDateRangeFormattedValue,
  getLandSizeRangeFormattedValue,
  getMarketMultiplySelectFormattedValue,
  getMultiplySelectFormattedValue,
  getNumberRangeFormattedValue,
  getPostalCodeSelectFormattedValue,
  getStateSelectFormattedValue,
  getTextFieldFormattedValue,
  getSelectFormattedValue,
  getSalePriceRangeFormattedValue,
  getCurrencyRangeFormattedValue,
  getBrokersSelectFormattedValue,
  getCompanyMultipleSelectFormattedValue,
  getContactNamesFormattedValue,
  getSwitchFormattedValue,
  getUseTypeMultipleSelectFormattedValue,
  getTransactionCategoryFormattedValue,
  getAccountMultiplySelectFormattedValue,
  getArrayOfObjectsFormattedValue,
  getSpecializationMultipleSelectFormattedValue,
  IFilterMemberRole,
  getMemberRoleAutocompleteFormattedValue,
  getStaticSetSelectFormattedValue,
  PresetOrRangeValueType,
} from '@/bundles/GroupFilters/helpers/formatters/inputs';
import { IFilterSelectedBroker } from '@/bundles/GroupFilters/interfaces/inputs/IFilterSelectedBroker';
import { IFilterSelectedCompany } from '@/bundles/GroupFilters/interfaces/inputs/IFilterSelectedCompany';
import { IFilterSelectedSpecialization } from '@/bundles/GroupFilters/interfaces/inputs/IFilterSelectedSpecialization';
import { IUseType } from '@/bundles/UseType/types';
import { IFilterSelectedAccount } from '@/bundles/GroupFilters/interfaces/inputs/IFilterSelectedAccount';
import { IMarketingTemplate } from '@/bundles/MarketingTemplate/types';
import { IFilterSelectedStaticSet } from '@/bundles/GroupFilters/interfaces/inputs/IFilterSelectedStaticSet';

const filterTypeDictionary = {
  [FilterTypeEnum.textfield]: (v: Nullable<string>) => getTextFieldFormattedValue(v),
  [FilterTypeEnum.company_name_combobox]: (v: Nullable<string>) => getTextFieldFormattedValue(v),
  [FilterTypeEnum.states_autocomplete]: (v: string[]) => getStateSelectFormattedValue(v),
  [FilterTypeEnum.postal_code_combobox]: (v: string[]) => getPostalCodeSelectFormattedValue(v),
  [FilterTypeEnum.one_select]: (v: string, config: IFilterConfigItem) => getSelectFormattedValue(v, config.items || []),
  [FilterTypeEnum.dynamic_market]: (v: string[]) => getMarketMultiplySelectFormattedValue(v),
  [FilterTypeEnum.land_size_range]: (v: [Nullable<number>, Nullable<number>]) => getLandSizeRangeFormattedValue(v),
  [FilterTypeEnum.date_range]: (v: PresetOrRangeValueType) => getDateRangeFormattedValue(v),
  [FilterTypeEnum.city_autocomplete]: (v: string[]) => getCityComboboxFormattedValue(v),
  [FilterTypeEnum.building_dimension_range]: (v: [Nullable<number>, Nullable<number>]) => getBuildingDimensionRangeFormattedValue(v),
  [FilterTypeEnum.number_range]: (v: [Nullable<string | number>, Nullable<string | number>], configFilter: IFilterConfigItem) => {
    return getNumberRangeFormattedValue(v, configFilter);
  },
  [FilterTypeEnum.create_updated_by_select]: (v: CreatedUpdatedByFilterValue) => getCreatedUpdatedBySelectFormattedValue(v),
  [FilterTypeEnum.member_role_autocomplete]: (v: IFilterMemberRole[]) => getMemberRoleAutocompleteFormattedValue(v),
  [FilterTypeEnum.sale_price_range]: (v: [Nullable<number>, Nullable<number>]) => getSalePriceRangeFormattedValue(v),
  [FilterTypeEnum.currency_range]: (v: [Nullable<number>, Nullable<number>]) => getCurrencyRangeFormattedValue(v),
  [FilterTypeEnum.brokers_select]: (v: IFilterSelectedBroker[]) => getBrokersSelectFormattedValue(v),
  [FilterTypeEnum.button_group]: (v: Nullable<string>, config: IFilterConfigItem | IFilterConfigChildren) => getSelectFormattedValue(v, config.items || []),
  [FilterTypeEnum.switch]: (v: boolean) => getSwitchFormattedValue(v),
  [FilterTypeEnum.autocomplete_multiple_select]: (v: string[] | number[], configFilter: IFilterConfigItem) => getMultiplySelectFormattedValue(v, configFilter),
  [FilterTypeEnum.company_multiple_select]: (v: IFilterSelectedCompany[]) => getCompanyMultipleSelectFormattedValue(v),
  [FilterTypeEnum.contact_names_multiple_select]: (v: string[]) => getContactNamesFormattedValue(v),
  [FilterTypeEnum.use_type_multiple_select]: (v: IUseType[]) => getUseTypeMultipleSelectFormattedValue(v),
  [FilterTypeEnum.specialization_multiple_select]: (v: Array<IFilterSelectedSpecialization | string>) => getSpecializationMultipleSelectFormattedValue(v),
  [FilterTypeEnum.transaction_category_checkbox_group]: (v: string[]) => getTransactionCategoryFormattedValue(v),
  [FilterTypeEnum.account_multiply_select]: (v: IFilterSelectedAccount[]) => getAccountMultiplySelectFormattedValue(v),
  [FilterTypeEnum.marketing_template_autocomplete]: (v: IMarketingTemplate[]) => getArrayOfObjectsFormattedValue(v),
  [FilterTypeEnum.static_set_select]: (v: IFilterSelectedStaticSet[]) => getStaticSetSelectFormattedValue(v),
}

export const getFormattedValueByConfig = (value: any, config: IFilterConfigItem | IFilterConfigChildren): string => {
  if (config.type in filterTypeDictionary) {
    return filterTypeDictionary[config.type](value, config);
  }

  return value;
}
